import React, { useMemo, useState } from 'react';
import { useStore } from '@/state/store';
import { useI18n } from '@/i18n/I18nContext';
import { buildAllPageSVGs } from '@/lib/svgExport';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { ChevronLeft, ChevronRight, Eye } from 'lucide-react';

// Read-only viewer for a document opened via a secure share link (used on phones
// for ANY share, and on desktop for "view only" shares). Renders every page as a
// fit-to-width SVG with simple page navigation. No editing is exposed.
export const ReadOnlyViewer = ({ doc }) => {
  const { t } = useI18n();
  const lang = useStore((s) => s.language);
  const pages = useMemo(() => buildAllPageSVGs(doc, lang), [doc, lang]);
  const [idx, setIdx] = useState(0);
  const safeIdx = Math.min(idx, Math.max(0, pages.length - 1));
  const page = pages[safeIdx];

  return (
    <div className="flex h-[100dvh] w-full flex-col bg-[#e9e9e4]" data-testid="readonly-viewer">
      <header className="flex items-center justify-between gap-2 border-b border-black/10 bg-[#0F1115] px-4 py-3 text-white">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#6EA8FF]">SwitchR</p>
          <p className="truncate text-sm font-medium" data-testid="readonly-viewer-title">
            {doc?.name || 'Diagramm'}
          </p>
        </div>
        <Badge className="shrink-0 gap-1 bg-white/10 text-white hover:bg-white/10">
          <Eye className="h-3 w-3" /> {t('viewer_readonly')}
        </Badge>
      </header>

      <div className="flex items-center gap-2 border-b border-black/10 bg-white px-3 py-2">
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0"
          disabled={safeIdx <= 0}
          onClick={() => setIdx((i) => Math.max(0, i - 1))}
          data-testid="readonly-viewer-prev"
          aria-label="prev"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <Select value={String(safeIdx)} onValueChange={(v) => setIdx(Number(v))}>
          <SelectTrigger className="h-9 flex-1" data-testid="readonly-viewer-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {pages.map((p, i) => (
              <SelectItem key={`${p.name}-${i}`} value={String(i)}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <span className="shrink-0 px-1 text-xs tabular-nums text-muted-foreground" data-testid="readonly-viewer-counter">
          {safeIdx + 1} {t('viewer_of')} {pages.length}
        </span>

        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0"
          disabled={safeIdx >= pages.length - 1}
          onClick={() => setIdx((i) => Math.min(pages.length - 1, i + 1))}
          data-testid="readonly-viewer-next"
          aria-label="next"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <main className="min-h-0 flex-1 overflow-auto p-3 sm:p-6">
        <div
          className="mx-auto w-full max-w-3xl bg-white shadow-lg ring-1 ring-black/5 lg:max-w-5xl [&>svg]:block [&>svg]:h-auto [&>svg]:w-full"
          dangerouslySetInnerHTML={{ __html: page?.svg || '' }}
        />
      </main>
    </div>
  );
};
